import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import styles from './Consolidate.module.css';
import { trackButtonClick, trackEvent } from '../utils/analytics';
import NewsletterSubscription from '../components/NewsletterSubscription';

export default function Consolidate() {
  const navigate = useNavigate();
  const [isNavigating, setIsNavigating] = useState(false);

  const handleClick = (e, path, trackingLabel = '') => {
    e.preventDefault();
    setIsNavigating(true);

    // Track button clicks for analytics
    if (trackingLabel) {
      trackButtonClick(trackingLabel, 'consolidate_page');
      trackEvent('click', 'consolidate_funnel', trackingLabel);
    }

    setTimeout(() => {
      navigate(path);
    }, 300);
  };

  const handleScrollToLeadCapture = e => {
    e.preventDefault();
    trackButtonClick('consolidate_lead_capture_scroll', 'consolidate_page');
    trackEvent('click', 'consolidate_funnel', 'consolidate_lead_capture_scroll');
    const el = document.getElementById('consolidate-lead-capture');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        {/* Hero Section - The Problem */}
        <section className={styles.heroSection}>
          <h1 className={styles.heroTitle}>Tired of Managing Multiple Address Books?</h1>
          <p className={styles.heroSubtitle}>
            You're not alone. The average person manages contacts across 6-8 different platforms.
          </p>
          <div className={styles.painPoints}>
            <div className={styles.painPoint}>
              <span className={styles.painIcon}>📱</span>
              <span>iPhone Contacts</span>
            </div>
            <div className={styles.painPoint}>
              <span className={styles.painIcon}>📧</span>
              <span>Gmail Contacts</span>
            </div>
            <div className={styles.painPoint}>
              <span className={styles.painIcon}>💼</span>
              <span>Outlook Contacts</span>
            </div>
            <div className={styles.painPoint}>
              <span className={styles.painIcon}>💻</span>
              <span>LinkedIn Connections</span>
            </div>
            <div className={styles.painPoint}>
              <span className={styles.painIcon}>👥</span>
              <span>Facebook Friends</span>
            </div>
            <div className={styles.painPoint}>
              <span className={styles.painIcon}>📋</span>
              <span>Business Cards</span>
            </div>
            <div className={styles.painPoint}>
              <span className={styles.painIcon}>📝</span>
              <span>Sticky Notes</span>
            </div>
          </div>
        </section>

        <section className={styles.guideSection}>
          <div className={styles.guideContent}>
            <h2 className={styles.guideTitle}>Free: The Contact Consolidation Guide</h2>
            <p className={styles.guideSubtitle}>
              Get the exact 3-step process to consolidate your address books without mixing personal
              and professional worlds.
            </p>

            <div className={styles.guideSteps}>
              <div className={styles.guideStep}>
                <div className={styles.guideStepNumber}>1</div>
                <div className={styles.guideStepBody}>
                  Export contacts from iCloud, Gmail, and Outlook.
                </div>
              </div>
              <div className={styles.guideStep}>
                <div className={styles.guideStepNumber}>2</div>
                <div className={styles.guideStepBody}>
                  Import into one database and merge duplicates.
                </div>
              </div>
              <div className={styles.guideStep}>
                <div className={styles.guideStepNumber}>3</div>
                <div className={styles.guideStepBody}>
                  Group by context so you share the right info with the right people.
                </div>
              </div>
            </div>

            <div className={styles.guideCtaRow}>
              <button
                onClick={handleScrollToLeadCapture}
                className={`${styles.guideCtaButton} ${isNavigating ? styles.buttonFadeOut : ''}`}
                type="button"
              >
                Send Me the Guide + Updates
              </button>
            </div>
          </div>
        </section>

        {/* The Chaos Section */}
        <section className={styles.chaosSection}>
          <h2 className={styles.sectionTitle}>The Result? Complete Chaos.</h2>
          <div className={styles.problemsGrid}>
            <div className={styles.problemCard}>
              <div className={styles.problemIcon}>🔍</div>
              <h3>Can't Find Anyone</h3>
              <p>
                "Was Sarah's number in my phone, Gmail, or that business card pile?" You waste
                precious time searching across multiple platforms.
              </p>
            </div>
            <div className={styles.problemCard}>
              <div className={styles.problemIcon}>📞</div>
              <h3>Outdated Information</h3>
              <p>
                Phone numbers change, people move, emails get updated. Your scattered contacts
                become a graveyard of dead information.
              </p>
            </div>
            <div className={styles.problemCard}>
              <div className={styles.problemIcon}>👥</div>
              <h3>Duplicate Contacts</h3>
              <p>
                The same person exists 3 times with different information. Which one is current?
                You'll never know until it's too late.
              </p>
            </div>
            <div className={styles.problemCard}>
              <div className={styles.problemIcon}>💔</div>
              <h3>Lost Connections</h3>
              <p>
                Important relationships slip through the cracks. That potential business partner?
                That old friend? Gone, because you couldn't find them. And nobody wants to sync
                Facebook with LinkedIn—mixing personal and professional is a nightmare.
              </p>
            </div>
          </div>
        </section>

        {/* The Cost Section */}
        <section className={styles.costSection}>
          <div className={styles.costContent}>
            <h2 className={styles.costTitle}>What's This Chaos Really Costing You?</h2>
            <div className={styles.costStats}>
              <div className={styles.costStat}>
                <span className={styles.costNumber}>2.5 hours</span>
                <span className={styles.costLabel}>wasted per week searching for contacts</span>
              </div>
              <div className={styles.costStat}>
                <span className={styles.costNumber}>$3,200</span>
                <span className={styles.costLabel}>in lost opportunities annually</span>
              </div>
              <div className={styles.costStat}>
                <span className={styles.costNumber}>67%</span>
                <span className={styles.costLabel}>
                  of professionals have outdated contact info
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* The Mixing Problem Section */}
        <section className={styles.mixingSection}>
          <div className={styles.mixingContent}>
            <h2 className={styles.mixingTitle}>The Mixing Nightmare</h2>
            <p className={styles.mixingDescription}>
              Ever tried syncing Facebook with LinkedIn? Or mixing your personal iPhone contacts
              with work Outlook?
              <strong> It's a disaster waiting to happen.</strong>
            </p>
            <div className={styles.mixingProblems}>
              <div className={styles.mixingProblem}>
                <span className={styles.mixingIcon}>😱</span>
                <span>Your boss sees your college party photos</span>
              </div>
              <div className={styles.mixingProblem}>
                <span className={styles.mixingIcon}>🤦</span>
                <span>Work colleagues get personal family updates</span>
              </div>
              <div className={styles.mixingProblem}>
                <span className={styles.mixingIcon}>💼</span>
                <span>Professional reputation at risk</span>
              </div>
            </div>
            <p className={styles.mixingConclusion}>
              <strong>You need separation AND organization</strong> — not forced platform syncing
              that destroys boundaries.
            </p>
          </div>
        </section>

        {/* The Solution Section */}
        <section className={styles.solutionSection}>
          <h2 className={styles.solutionTitle}>There's a Better Way</h2>
          <p className={styles.solutionSubtitle}>
            Imagine having all your contacts in one place, perfectly organized, always up-to-date.
          </p>

          <div className={styles.solutionDemo}>
            <div className={styles.beforeAfter}>
              <div className={styles.before}>
                <h3>Before: The Mess</h3>
                <div className={styles.messyContacts}>
                  <div className={styles.scatteredContact}>📱 John (555) 123-4567</div>
                  <div className={styles.scatteredContact}>📧 john.smith@oldcompany.com</div>
                  <div className={styles.scatteredContact}>💼 John Smith - Marketing Dir.</div>
                  <div className={styles.scatteredContact}>📋 John - (555) 987-6543</div>
                </div>
              </div>
              <div className={styles.arrow}>→</div>
              <div className={styles.after}>
                <h3>After: Perfect Organization</h3>
                <div className={styles.organizedContact}>
                  <div className={styles.contactCard}>
                    <h4>John Smith</h4>
                    <p>Marketing Director at TechCorp</p>
                    <p>📱 (555) 123-4567 (Current)</p>
                    <p>📧 john.smith@techcorp.com</p>
                    <p>🏢 Work • 👥 Business Network • ⭐ VIP</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features That Solve Problems */}
        <section className={styles.featuresSection}>
          <h2 className={styles.featuresTitle}>
            MyPeopleDatabase: Your Contact Consolidation Solution
          </h2>
          <div className={styles.featuresGrid}>
            <div className={styles.featureCard}>
              <div className={styles.featureIcon}>🎯</div>
              <h3>One Central Hub</h3>
              <p>
                Import from all your scattered sources into one unified database. No more hunting
                across platforms.
              </p>
            </div>
            <div className={styles.featureCard}>
              <div className={styles.featureIcon}>🏷️</div>
              <h3>Smart Organization</h3>
              <p>
                Create custom groups, tags, and categories. Find anyone instantly with powerful
                search and filtering.
              </p>
            </div>
            <div className={styles.featureCard}>
              <div className={styles.featureIcon}>🔄</div>
              <h3>Duplicate Detection</h3>
              <p>
                Automatically identifies and helps you merge duplicate contacts. One person, one
                complete record.
              </p>
            </div>
            <div className={styles.featureCard}>
              <div className={styles.featureIcon}>🔒</div>
              <h3>Your Data, Your Control</h3>
              <p>
                Unlike cloud services, your contacts stay on your computer. No privacy concerns, no
                data mining.
              </p>
            </div>
            <div className={styles.featureCard}>
              <div className={styles.featureIcon}>📊</div>
              <h3>Rich Contact Profiles</h3>
              <p>
                Store everything: multiple phone numbers, addresses, notes, relationships, and
                important dates.
              </p>
            </div>
            <div className={styles.featureCard}>
              <div className={styles.featureIcon}>⚡</div>
              <h3>Lightning Fast</h3>
              <p>
                Desktop application means instant access. No loading screens, no internet required
                for basic functions.
              </p>
            </div>
          </div>
        </section>

        {/* Social Proof */}
        <section className={styles.socialProofSection}>
          <h2 className={styles.socialProofTitle}>Join Thousands Who've Already Consolidated</h2>
          <div className={styles.testimonials}>
            <div className={styles.testimonial}>
              <p>
                "Finally! I went from 6 different contact lists to one organized database. Found my
                old college roommate in seconds."
              </p>
              <cite>— Sarah M., Marketing Professional</cite>
            </div>
            <div className={styles.testimonial}>
              <p>
                "The duplicate detection saved me hours. It found contacts I didn't even know I had
                duplicated across platforms."
              </p>
              <cite>— David R., Sales Manager</cite>
            </div>
            <div className={styles.testimonial}>
              <p>
                "My networking game is completely different now. I can actually follow up with
                people because I can find them!"
              </p>
              <cite>— Lisa K., Entrepreneur</cite>
            </div>
          </div>
        </section>

        {/* Urgency Section */}
        <section className={styles.urgencySection}>
          <h2 className={styles.urgencyTitle}>Stop Losing Valuable Connections Today</h2>
          <p className={styles.urgencyText}>
            Every day you wait is another day of missed opportunities, lost time, and growing
            contact chaos. The solution is here, ready to transform how you manage your
            relationships.
          </p>
          <div className={styles.urgencyStats}>
            <div className={styles.urgencyStat}>
              <span className={styles.urgencyNumber}>Today</span>
              <span className={styles.urgencyLabel}>You'll waste 20+ minutes searching</span>
            </div>
            <div className={styles.urgencyStat}>
              <span className={styles.urgencyNumber}>This Week</span>
              <span className={styles.urgencyLabel}>2.5 hours of lost productivity</span>
            </div>
            <div className={styles.urgencyStat}>
              <span className={styles.urgencyNumber}>This Month</span>
              <span className={styles.urgencyLabel}>Potential deals slip away</span>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className={styles.ctaSection}>
          <div className={styles.ctaContent}>
            <h2 className={styles.ctaTitle}>Ready to Consolidate Your Contacts?</h2>
            <p className={styles.ctaSubtitle}>
              Join thousands of professionals who've already organized their contact chaos with
              MyPeopleDatabase.
            </p>

            <div className={styles.ctaButtons}>
              <button
                onClick={e => handleClick(e, '/buy', 'consolidate_to_buy_primary')}
                className={`${styles.primaryCta} ${isNavigating ? styles.buttonFadeOut : ''}`}
              >
                Get MyPeopleDatabase Now
              </button>
              <button
                onClick={e => handleClick(e, '/download', 'consolidate_to_download')}
                className={`${styles.secondaryCta} ${isNavigating ? styles.buttonFadeOut : ''}`}
              >
                Try Free Download First
              </button>
            </div>

            <div className={styles.ctaFeatures}>
              <div className={styles.ctaFeature}>
                <span className={styles.checkmark}>✓</span>
                <span>Import from all major platforms</span>
              </div>
              <div className={styles.ctaFeature}>
                <span className={styles.checkmark}>✓</span>
                <span>Automatic duplicate detection</span>
              </div>
              <div className={styles.ctaFeature}>
                <span className={styles.checkmark}>✓</span>
                <span>Complete privacy & data ownership</span>
              </div>
              <div className={styles.ctaFeature}>
                <span className={styles.checkmark}>✓</span>
                <span>Lifetime access with one-time purchase</span>
              </div>
            </div>

            <div id="consolidate-lead-capture" className={styles.leadCapture}>
              <NewsletterSubscription />
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
